import { ReactNode, useState, useEffect } from 'react';
import { Sidebar } from './Sidebar';
import { MobileNav } from './MobileNav';
import { useRealtimeNotifications } from '@/hooks/useRealtimeNotifications';
import { useUserRole } from '@/hooks/useUserRole';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ShieldAlert, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ForcePasswordChange } from '@/components/auth/ForcePasswordChange';

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { isAdmin } = useUserRole();
  const { user } = useAuth();
  const [mustChangePassword, setMustChangePassword] = useState(false);

  useEffect(() => {
    setMustChangePassword(user?.user_metadata?.must_change_password === true);
  }, [user]);

  // Enable realtime notifications for admins
  useRealtimeNotifications();

  // Check approval gate status for admins
  const { data: approvalsPaused } = useQuery({
    queryKey: ['approvals-paused'],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('loan_config')
        .select('approvals_paused')
        .limit(1)
        .single();
      return (data as any)?.approvals_paused === true;
    },
    enabled: isAdmin,
    staleTime: 30_000,
  });
  
  return (
    <div className="flex h-screen bg-background">
      <div className="hidden md:flex">
        <Sidebar />
      </div>
      <main className="flex-1 overflow-auto">
        <div className="flex items-center justify-between px-4 py-2 border-b bg-background sticky top-0 z-30">
          <MobileNav />
          <Button variant="ghost" size="sm" asChild>
            <Link to="/web">
              <Globe className="h-4 w-4 mr-1.5" />
              <span className="hidden sm:inline">Zobrazit web</span>
            </Link>
          </Button>
        </div>
        {isAdmin && approvalsPaused && (
          <div className="bg-destructive text-destructive-foreground px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-2">
            <ShieldAlert className="h-4 w-4" />
            APPROVAL GATE AKTIVNÍ — Žádné automatické schvalování
          </div>
        )}
        <div className="container py-4 sm:py-6 max-w-7xl px-3 sm:px-6">
          {children}
        </div>
      </main>
      <ForcePasswordChange open={mustChangePassword} onPasswordChanged={() => setMustChangePassword(false)} />
    </div>
  );
}
